/**
 * I corpi in ingresso del banco: i gesti della ricezione.
 *
 * Le letture corrispondenti stanno in `risposte.ts`; qui c'è solo ciò che il
 * banco **manda**.
 */
import { z } from 'zod';
import { zCodiceCassetta, zTaglia } from './primitives.js';
export const zAgganciaCapo = z.object({
    ordine: z.string().min(1),
    riga: z.string().min(1),
});
/**
 * Il gesto della regola 2-bis: si dichiara **il capo che si ha in mano**, non
 * la riga d'ordine. Sul capo appena pescato dalla scatola non c'è ancora
 * un'etichetta, quindi articolo e taglia sono tutto ciò che si conosce — ed è
 * il sistema a dover trovare l'ordine più vecchio che lo aspetta.
 */
export const zScansionaCapo = z.object({
    sku: z.string().min(1),
    taglia: zTaglia,
});
/**
 * La strada che parte dalla bolla di carta: si dichiara **quale arrivo** il
 * banco sta ricevendo, invece di lasciarlo dedurre dal capo scansionato.
 *
 * Il numero è quello del PO stampato sul DDT, e qui non si valida oltre la
 * lunghezza: che esista davvero non lo può sapere uno schema — lo dice
 * DecoNetwork, e la risposta è un rifiuto da mostrare, non un 400.
 */
export const zApriArrivo = z.object({
    numero: z.string().min(1).max(32),
});
export const zApriCassetta = z.object({
    cassetta: zCodiceCassetta,
});
/**
 * Without `quantita` it is the tap: one garment. With it, the same gesture
 * typed once, for the box of forty polos. Whether they all fit is the domain's
 * call: here only what is not a garment count is refused.
 */
export const zContaCapo = z.object({
    taglia: zTaglia,
    quantita: z.number().int().positive().optional(),
});
/** Corrects how many garments of a size arrived on a line (zero is allowed). */
export const zImpostaRicevuti = z.object({
    chiave: z.string().min(1),
    taglia: zTaglia,
    quantita: z.number().int().min(0),
});
/** Closures whose DecoNetwork write is still to be done. */
export const zChiusureInSospeso = z.array(z.object({
    id: z.string(),
    numero: z.string(),
    motivo: z.string(),
    tipo: z.enum(['express', 'finale']),
}));
/** The `−` removes one garment at a time: there is no quantity here. */
export const zTogliCapo = z.object({
    taglia: zTaglia,
});
/**
 * Il fallato porta con sé **l'id** della foto, non i byte e non l'indirizzo.
 *
 * I byte no perché la foto viaggia per conto suo, su una rotta multipart: la
 * segnalazione del fallato è un gesto attribuito, quindi il primo tentativo
 * torna 403 e il trasporto lo **ritenta** dopo la conferma della password —
 * ritentare un megabyte sul Wi-Fi del reparto è un affare peggiore che
 * ritentare un JSON con dentro un uuid.
 *
 * L'indirizzo no perché lo compone l'API da sé: una stringa di percorso che
 * arriva da fuori è una stringa di percorso da validare, e qui non c'è niente
 * da guadagnarci.
 *
 * Facoltativa, come dappertutto: la foto si chiede e non si pretende (regola
 * 38-bis), e un fallato che non parte perché la webcam è guasta lascia contata
 * come buona della merce che non lo è.
 */
export const zSegnaFallato = z.object({
    taglia: zTaglia,
    quantita: z.number().int().positive(),
    foto: z.string().uuid().optional(),
});
/**
 * Lo scatto al banco (regola 38-bis). Il corpo di testo di una richiesta
 * multipart: l'immagine viaggia nella parte `foto`.
 *
 * **Non dichiara di quale riga sia la foto**, ed è la decisione che questo
 * schema porta: il soggetto è il capo agganciato, che il banco già conosce.
 * Lasciarlo dire a chi manda vorrebbe dire permettere a una schermata rimasta
 * indietro di attaccare una foto alla variante sbagliata — e una foto
 * etichettata male è l'unico guasto che avvelena l'archivio in silenzio.
 *
 * La taglia è facoltativa perché la foto è **della variante**, non del capo
 * singolo: in una fotografia la taglia non si vede. Si dichiara solo dove
 * serve, cioè sul fallato, dove la segnalazione all'ufficio acquisti ne nomina
 * una precisa.
 */
export const zScattaFoto = z.object({
    taglia: zTaglia.optional(),
});
export const zMarcaPronte = z.object({
    cassette: z.array(zCodiceCassetta).min(1),
});
// --- Where the express goes (rule 47, «Dove lo metto») ---
/** Pieces per express box of a DecoNetwork product, as the bench writes it. */
export const zCapienzaScatola = z.object({
    pezzi: z.number().int().min(1).max(999),
});
/** The product of `PUT ricezione/express/capienze/:productId`: a DecoNetwork product id. */
export const zIdProdotto = z
    .string()
    .regex(/^\d+$/)
    .transform(Number)
    .pipe(z.number().int().min(1).max(2147483647));
const zPostoScatola = z.object({
    kind: z.enum(['box', 'new']),
    drawer: z.number().int(),
    x: z.number().int(),
    y: z.number().int(),
});
/** One piece of the plan: so many pieces of an article in one box (existing or new). */
export const zAssegnazionePosto = z.object({
    id: z.string().min(1),
    article: z.string().min(1),
    pieces: z.number().int().min(1),
    target: zPostoScatola,
});
/** The operator's plan, sent to be checked. Two choices with one id would be one choice told twice. */
export const zScelteExpress = z.object({
    scelte: z
        .array(zAssegnazionePosto)
        .max(1000)
        .superRefine((scelte, ctx) => {
        const visti = new Set();
        for (const [i, s] of scelte.entries()) {
            if (visti.has(s.id)) {
                ctx.addIssue({
                    code: z.ZodIssueCode.custom,
                    path: [i, 'id'],
                    message: `Scelta ripetuta: ${s.id}`,
                });
            }
            visti.add(s.id);
        }
    }),
});
/**
 * The optional body of «Carica l'express» and «Chiudi bolla»: the plan the
 * operator confirmed, and the ids of the warnings confirmed a second time.
 */
export const zConfermaPosti = z.object({
    scelte: zScelteExpress.shape.scelte,
    confermati: z.array(z.string().min(1)).max(1000),
});
/** No body, or an empty one, is no choice: `null`. */
export const zCorpoCaricaExpress = z.preprocess((v) => v === undefined ||
    v === null ||
    (typeof v === 'object' && !Array.isArray(v) && Object.keys(v).length === 0)
    ? null
    : v, zConfermaPosti.nullable());
const zArea = {
    drawer: z.number().int(),
    x: z.number().int(),
    y: z.number().int(),
    width: z.number().int(),
    depth: z.number().int(),
};
const zPezziArticolo = z.object({ article: z.string(), pieces: z.number().int() });
/** The proposal and the check of «Dove lo metto», drawn on the drawers of Modula 1. */
export const zEsitoPosti = z.discriminatedUnion('esito', [
    /** The drawers could not be read (or Modula 1 came back empty): nothing starts. */
    z.object({ esito: z.literal('cassetti-illeggibili') }),
    z.object({
        esito: z.literal('proposta'),
        proposal: z.object({
            assignments: z.array(zAssegnazionePosto).readonly(),
            leftovers: z.array(zPezziArticolo).readonly(),
            missingCapacity: z.array(z.number().int()).readonly(),
            unknownArticles: z.array(z.string()).readonly(),
            needsOperator: z.boolean(),
        }),
        check: z.discriminatedUnion('ok', [
            z.object({
                ok: z.literal(true),
                warnings: z
                    .array(z.object({
                    id: z.string(),
                    kind: z.enum(['size', 'dark-colors']),
                    assignment: z.string(),
                    text: z.string(),
                }))
                    .readonly(),
            }),
            z.object({ ok: z.literal(false), problems: z.array(z.string()).readonly() }),
        ]),
        boxes: z
            .array(z.object({
            ...zArea,
            group: z.string().nullable(),
            container: z.enum(['box', 'strip-box', 'other']),
            contents: z.array(zPezziArticolo).readonly(),
        }))
            .readonly(),
        slots: z.array(z.object(zArea)).readonly(),
        /** Keyed by Modula article code (the DecoNetwork SKU). */
        facts: z.record(z.object({
            article: z.string(),
            dnSkuId: z.string(),
            productId: z.number().int(),
            productName: z.string(),
            size: z.string(),
            color: z.string(),
        })),
        /** Pieces per box of the products in `facts`, keyed by product id. */
        capacities: z.record(z.number().int()),
        /** Why some express pieces are left out of the proposal, said on screen. */
        unknownReasons: z.array(z.string()).readonly(),
    }),
]);
/** What the bench gets: the proposal, or a refusal of the bench (no note, no express…). */
export const zPostiExpress = z.union([
    z.object({
        ok: z.literal(false),
        titolo: z.string(),
        motivo: z.string(),
        regola: z.string(),
    }),
    zEsitoPosti,
]);
//# sourceMappingURL=receiving.js.map